import { createSelector, createSlice } from '@reduxjs/toolkit'
import {
  baseReducers,
  getIdOrModelId,
  SBAPI,
  SBSelectRaw,
  SBState,
} from '@supplement-bacon/react-toolkit/utils'
import { initialQuery, TableParams } from '@supplement-bacon/sb-table-filters'
import { FeaturedVenue } from '../models/FeaturedVenue'
import { featuredVenueSchema } from '../models/schema'
import {
  createFeaturedVenues as createFeaturedVenuesAPI,
  deleteFeaturedVenues as deleteFeaturedVenuesAPI,
  fetchFeaturedVenues,
  fetchFeaturedVenuesById,
  updateFeaturedVenues as updateFeaturedVenuesAPI,
} from '../services/FeaturedVenuesService'
import { AppDispatch, RootState } from './store'

const initialState: SBState<FeaturedVenue> = {
  isLoading: false,
  error: null,
  items: {},
  ids: [],
  selectedId: undefined,
  query: initialQuery,
}

const slice = createSlice({
  name: 'featuredVenues',
  initialState,
  reducers: baseReducers,
})

// Reducer
export default slice.reducer
export const {
  getItemsSuccess: getFeaturedVenuesSuccess,
  setQuery: setFeaturedVenuesQuery,
  reset: resetFeaturedVenuesState,
  resetQueryAndIds: resetFeaturedVenuesQueryAndIds,
} = slice.actions

/**
 * Selectors
 */

const selectRawItems: SBSelectRaw<
  RootState,
  { [key: number]: FeaturedVenue }
> = (state: RootState) => state[slice.name].items
const selectRawIds: SBSelectRaw<RootState, number[]> = (state: RootState) =>
  state[slice.name].ids
const selectRawSelectedId: SBSelectRaw<RootState, number | undefined> = (
  state: RootState
) => state[slice.name].selectedId

export const selectFeaturedVenues = () =>
  createSelector(
    [selectRawItems, selectRawIds],
    (items, ids) => ids.map((id) => items[id]).filter((i) => i)
    // Filter allow to return only non-null elements
  )
export const selectSelectedFeaturedVenues = () =>
  createSelector([selectRawItems, selectRawSelectedId], (items, id) =>
    id !== undefined ? items[id] : undefined
  )
export const selectFeaturedVenuesById = (id: number) =>
  createSelector([selectRawItems], (items) =>
    items.hasOwnProperty(id) ? items[id] : undefined
  )
export const selectFeaturedVenuesByIds = (ids: number[]) =>
  createSelector([selectRawItems], (items) =>
    ids.filter((id) => items.hasOwnProperty(id)).map((id) => items[id])
  )

/**
 * Actions
 */

export const setSelectedFeaturedVenues =
  (featuredVenues: FeaturedVenue | number) => async (dispatch: AppDispatch) =>
    dispatch(
      slice.actions.setSelectedId(getIdOrModelId<FeaturedVenue>(featuredVenues))
    )

export const getFeaturedVenues = (params: TableParams) =>
  SBAPI.FetchPaginatedDispatched(
    fetchFeaturedVenues(params),
    [featuredVenueSchema],
    slice.actions
  )

export const createFeaturedVenues = (featuredVenues: FeaturedVenue) =>
  SBAPI.CreateDispatched(
    createFeaturedVenuesAPI(featuredVenues),
    featuredVenueSchema,
    slice.actions
  )

export const getFeaturedVenuesWithId = (id: number) =>
  SBAPI.FetchDispatched<FeaturedVenue>(
    fetchFeaturedVenuesById(id),
    featuredVenueSchema,
    slice.actions
  )

export const updateFeaturedVenues = (featuredVenues: FeaturedVenue) =>
  SBAPI.UpdateDispatched<FeaturedVenue>(
    updateFeaturedVenuesAPI(featuredVenues),
    featuredVenues,
    slice.actions
  )

export const deleteFeaturedVenues = (featuredVenues: FeaturedVenue) =>
  SBAPI.DestroyDispatched<FeaturedVenue>(
    deleteFeaturedVenuesAPI(featuredVenues),
    featuredVenues,
    slice.actions
  )
